import { Link } from "react-router-dom";
import ScrollAnimation from "../../common/ScrollAnimation";
import TypewriterText from "../../common/TypewriterText";
import leftSvg from "../../../assets/image/Group 4 (1).svg";
import rightSvg from "../../../assets/image/Group 11.svg";
import HeroBackground from "./HeroBackground";
import { Sparkles, Search, Download, Users } from "lucide-react";
import {
  rotatingTexts,
  heroContent,
} from "../../../data/landingPage/heroContent";
import useLandingMotion from "../../../hooks/useLandingMotion";
import { LANDING_CONTAINER_PADDED } from "../../../constants/landingPageLayout";

const HERO_TYPEWRITER_STYLE = {
  backgroundImage: "linear-gradient(180deg, #7D3BEE 0%, #4F46E5 100%)",
  WebkitBackgroundClip: "text",
  backgroundClip: "text",
  color: "transparent",
  WebkitTextFillColor: "transparent",
};

const Hero = () => {
  const { isStatic } = useLandingMotion();

  return (
    <section className="relative w-full clip-x-visible-y bg-white">
      {/* Decorative blobs — scale from 1440px Figma frame, all breakpoints */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 z-0 hidden h-full overflow-visible lg:block"
        aria-hidden="true"
      >
        {/* Left decorative blob — 35% visible, peeking from left edge */}
        <img
          src={leftSvg}
          alt=""
          className="absolute select-none opacity-100"
          style={{
            width: "clamp(12rem, 34vw, 490px)",
            height: "clamp(12rem, 34vw, 490px)",
            top: "clamp(3rem, 7.5vw, 110px)",
            left: "clamp(-20.8rem, -22.1vw, -38.5px)",
          }}
        />

        {/* Right decorative blob — 35% visible, peeking from right edge */}
        <img
          src={rightSvg}
          alt=""
          className="absolute select-none opacity-100"
          style={{
            width: "clamp(13rem, 37vw, 534px)",
            height: "clamp(13rem, 37vw, 534px)",
            top: "clamp(2.5rem, 5.5vw, 85px)",
            right: "clamp(-24.45rem, -24.05vw, -347.1px)",
          }}
        />
      </div>

      {/* Hero background illustration — inline SVG for lens & character motion */}
      <HeroBackground isStatic={isStatic} />

      {/* Decorative blobs — mobile/tablet (smaller corner accents, above the bg image) */}
      <div
        className="pointer-events-none absolute inset-0 z-0 overflow-hidden lg:hidden"
        aria-hidden="true"
      >
        {/* Left blob — mirrored from the right blob */}
        <img
          src={leftSvg}
          alt=""
          className="absolute select-none"
          style={{
            width: "231.06px",
            height: "231.06px",
            top: "-40px",
            left: "-183.33px",
            opacity: 1,
          }}
        />
        {/* Right blob — exact Figma layout (anchored to right edge) */}
        <img
          src={rightSvg}
          alt=""
          className="absolute select-none"
          style={{
            width: "231.06px",
            height: "231.06px",
            top: "140.54px",
            right: "-183.33px",
            opacity: 1,
          }}
        />
      </div>

      {/* Candidates & Platforms — desktop, aligned with navbar padding */}
      <div
        className="absolute z-20 inset-x-0 hidden lg:block"
        style={{
          top: "673px",
        }}
      >
        <div
          className={`${LANDING_CONTAINER_PADDED} flex h-[81px] items-center justify-between opacity-100`}
        >
        {/* 20k+ Candidates */}
        <div className="flex flex-col items-start">
          <span className="font-semibold text-slate-900 text-[16px]">
            20k+ <span className="text-slate-600 font-medium">Candidates</span>
          </span>
          <div className="flex -space-x-3 mt-2.5">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="w-9 h-9 rounded-full border-2 border-white bg-slate-200 overflow-hidden shadow-sm flex-shrink-0"
              >
                {isStatic ? (
                  <span className="flex h-full w-full items-center justify-center bg-indigo-100 text-[10px] font-semibold text-indigo-600">
                    {String.fromCharCode(65 + i)}
                  </span>
                ) : (
                  <img
                    src={`https://i.pravatar.cc/100?img=${i + 12}`}
                    alt="Candidate avatar"
                    loading="lazy"
                    width={36}
                    height={36}
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
            ))}
            <div className="w-9 h-9 rounded-full border-2 border-white bg-[#6366F1] flex items-center justify-center text-white text-sm font-bold shadow-sm flex-shrink-0">
              +
            </div>
          </div>
        </div>

        {/* 50+ Platforms */}
        <div className="flex flex-col items-start">
          <span className="font-semibold text-slate-900 text-[16px]">
            50+ <span className="text-slate-600 font-medium">Platforms</span>
          </span>
          <div className="flex -space-x-2 mt-2.5">
            <div className="w-9 h-9 rounded-full border-2 border-white bg-[#0A66C2] flex items-center justify-center text-white text-sm font-bold shadow-sm flex-shrink-0">
              in
            </div>
            <div
              className="w-9 h-9 rounded-full border-2 border-white bg-black flex items-center justify-center text-white text-base font-bold shadow-sm flex-shrink-0"
              style={{ fontFamily: "serif" }}
            >
              𝕏
            </div>
            <div className="w-9 h-9 rounded-full border-2 border-white bg-gradient-to-tr from-[#f09433] via-[#e6683c] to-[#bc1888] flex items-center justify-center text-white text-xs font-bold shadow-sm flex-shrink-0">
              ig
            </div>
            <div className="w-9 h-9 rounded-full border-2 border-white bg-[#6366F1] flex items-center justify-center text-white text-sm font-bold shadow-sm flex-shrink-0">
              +
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Main Content Container */}
      <div
        className={`relative z-[1] ${LANDING_CONTAINER_PADDED} flex min-h-0 w-full flex-col items-center pb-[60px] pt-[96px] lg:min-h-[1030px] lg:pt-[100px]`}
      >
        {/* Center Text Section */}
        <ScrollAnimation
          animationType="fadeUp"
          delay={0}
          className="w-full flex flex-col items-center"
        >
          <div className="w-full max-w-[710.66px] flex flex-col items-center text-center gap-[18px] z-10 relative">
            {/* Soft background glow */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/80 to-transparent pointer-events-none -z-10" />

            {/* Badge */}
            {/* <div className="h-[36px] px-4 bg-[#F2FF00]/40 rounded-full flex items-center justify-center text-[#724BEF] text-[14px] font-semibold tracking-wide select-none">
              {heroContent.badge.text}
            </div> */}

            {/* Heading with Static Text & Figma Select Box Effect */}
            <h1 className="text-[#0F172A] font-extrabold text-[32px] sm:text-[44px] md:text-[56px] leading-[1.3] tracking-tight">
              Streamline Your <br className="hidden sm:block" />
              <span
                className="relative inline-flex shrink-0 items-center justify-center border px-4 md:px-8 mx-2 bg-[#6366F1]/10 align-middle"
                style={{
                  transform: "rotate(-2deg)",
                  height: "clamp(50px, 8vw, 88px)",
                  width: "clamp(200px, 30vw, 423px)",
                  borderStyle: "solid",
                  borderWidth: "1px",
                  borderImageSource:
                    "linear-gradient(180deg, #7D3BEE 0%, #4F46E5 100%)",
                  borderImageSlice: 1,
                }}
              >
                <span className="pointer-events-none absolute w-[8px] h-[8px] bg-gradient-to-b from-[#7D3BEE] to-[#4F46E5] -top-[5px] -left-[5px] z-10" />
                <span className="pointer-events-none absolute w-[8px] h-[8px] bg-gradient-to-b from-[#7D3BEE] to-[#4F46E5] -top-[5px] -right-[5px] z-10" />
                <span className="pointer-events-none absolute w-[8px] h-[8px] bg-gradient-to-b from-[#7D3BEE] to-[#4F46E5] -bottom-[5px] -left-[5px] z-10" />
                <span className="pointer-events-none absolute w-[8px] h-[8px] bg-gradient-to-b from-[#7D3BEE] to-[#4F46E5] -bottom-[5px] -right-[5px] z-10" />
                <TypewriterText
                  texts={rotatingTexts}
                  typingSpeed={85}
                  deletingSpeed={45}
                  pauseDuration={2200}
                  className="font-bold"
                  style={HERO_TYPEWRITER_STYLE}
                  cursorClassName="typewriter-cursor !bg-[#6366F1]"
                />
              </span>
              Process
            </h1>

            {/* Description */}
            <p className="text-[#64748B] text-base md:text-lg max-w-[600px] leading-relaxed">
              {heroContent.description}
            </p>

            {/* Buttons Row */}
            <div className="flex flex-wrap items-center gap-3 justify-center mt-2">
              <Link
                to="/how-it-works"
                className="h-[44px] px-6 border border-slate-200 bg-white hover:bg-slate-50 text-slate-800 font-medium text-[15px] rounded-full flex items-center justify-center transition-all duration-200 shadow-sm"
              >
                How It Works
              </Link>
              <span className="inline-flex items-center gap-0">
                <Link
                  to="/signup"
                  className="inline-flex h-[39.52px] items-center justify-center whitespace-nowrap rounded-[17.89px] bg-[#4F46E5] px-[10.44px] text-[13.42px] font-medium leading-none tracking-normal text-white shadow-md transition-colors duration-200 hover:bg-[#4338CA]"
                  style={{ fontFamily: '"Inter", sans-serif' }}
                >
                  Start Free Trial
                </Link>
                <Link
                  to="/signup"
                  aria-label="Start Free Trial"
                  className="inline-flex h-[39.52px] w-[39.52px] shrink-0 items-center justify-center rounded-full bg-[#4F46E5] p-[7.46px] text-white shadow-md transition-colors duration-200 hover:bg-[#4338CA]"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25"
                    />
                  </svg>
                </Link>
              </span>
            </div>
          </div>
        </ScrollAnimation>

        {/* Desktop spacer — reserves space below absolute hero image */}
        <div
          className="hidden w-full shrink-0 lg:block"
          style={{ height: "calc(100vw * 338 / 1280)", marginTop: "40px" }}
          aria-hidden="true"
        />

        {/* Mobile / tablet stats */}
        <div className="relative z-20 mt-8 hidden w-full flex-col items-center justify-between gap-8 sm:flex sm:flex-row sm:gap-0 lg:hidden">
          <div className="flex flex-col items-center sm:items-start">
            <span className="font-semibold text-slate-900 text-[16px]">
              20k+{" "}
              <span className="text-slate-600 font-medium">Candidates</span>
            </span>
            <div className="flex -space-x-3 mt-2.5">
              {[...Array(4)].map((_, i) => (
                <div
                  key={`mobile-candidate-${i}`}
                  className="w-9 h-9 rounded-full border-2 border-white bg-slate-200 overflow-hidden shadow-sm flex-shrink-0"
                >
                  {isStatic ? (
                    <span className="flex h-full w-full items-center justify-center bg-indigo-100 text-[10px] font-semibold text-indigo-600">
                      {String.fromCharCode(65 + i)}
                    </span>
                  ) : (
                    <img
                      src={`https://i.pravatar.cc/100?img=${i + 12}`}
                      alt="Candidate avatar"
                      loading="lazy"
                      width={36}
                      height={36}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
              ))}
              <div className="w-9 h-9 rounded-full border-2 border-white bg-[#6366F1] flex items-center justify-center text-white text-sm font-bold shadow-sm flex-shrink-0">
                +
              </div>
            </div>
          </div>
          <div className="flex flex-col items-center sm:items-start">
            <span className="font-semibold text-slate-900 text-[16px]">
              50+ <span className="text-slate-600 font-medium">Platforms</span>
            </span>
            <div className="flex -space-x-2 mt-2.5">
              <div className="w-9 h-9 rounded-full border-2 border-white bg-[#0A66C2] flex items-center justify-center text-white text-sm font-bold shadow-sm flex-shrink-0">
                in
              </div>
              <div
                className="w-9 h-9 rounded-full border-2 border-white bg-black flex items-center justify-center text-white text-base font-bold shadow-sm flex-shrink-0"
                style={{ fontFamily: "serif" }}
              >
                𝕏
              </div>
              <div className="w-9 h-9 rounded-full border-2 border-white bg-gradient-to-tr from-[#f09433] via-[#e6683c] to-[#bc1888] flex items-center justify-center text-white text-xs font-bold shadow-sm flex-shrink-0">
                ig
              </div>
              <div className="w-9 h-9 rounded-full border-2 border-white bg-[#6366F1] flex items-center justify-center text-white text-sm font-bold shadow-sm flex-shrink-0">
                +
              </div>
            </div>
          </div>
        </div>

        {/* "Everything You Need To Source Faster" Bottom Card */}
        <ScrollAnimation
          animationType="fadeUp"
          delay={350}
          className="w-full flex justify-center"
        >
          <div className="w-full max-w-[1145px] bg-gradient-to-b from-white via-[#F4F0F9] to-white border border-slate-200 border-b-[#E2E8F0] shadow-[0px_-5px_4px_0px_#00000005] rounded-b-[20px] rounded-t-[10px] p-[30px] md:p-[40px] mt-[40px] lg:mt-0 z-10">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-[54px] w-full max-w-[1071px] mx-auto">
              {/* Left text block */}
              <div className="flex flex-col items-center lg:items-start text-center lg:text-left max-w-[450px]">
                <h3 className="text-[26px] md:text-[32px] font-extrabold text-[#0F172A] leading-tight">
                  Everything You Need
                  <span
                    className="block"
                    style={{
                      backgroundImage:
                        "linear-gradient(180deg, #7D3BEE 0%, #4F46E5 100%)",
                      WebkitBackgroundClip: "text",
                      backgroundClip: "text",
                      color: "transparent",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    To Source Faster
                  </span>
                </h3>
                <p className="text-[#64748B] text-[15px] mt-2.5 leading-relaxed">
                  Streamline your recruitment process with powerful tools
                  designed for modern teams.
                </p>
              </div>

              {/* Right Features Grid (2x2) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-[54px] gap-y-6 w-full lg:w-auto">
                {/* Feature 1 */}
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#6366F1] flex items-center justify-center text-white shadow-sm flex-shrink-0">
                    <Sparkles className="w-4.5 h-4.5" />
                  </div>
                  <span className="font-semibold text-slate-800 text-[15px]">
                    Auto-fetch Sources
                  </span>
                </div>

                {/* Feature 2 */}
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#3B82F6] flex items-center justify-center text-white shadow-sm flex-shrink-0">
                    <Search className="w-4.5 h-4.5" />
                  </div>
                  <span className="font-semibold text-slate-800 text-[15px]">
                    Search & Shortlist
                  </span>
                </div>

                {/* Feature 3 */}
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#3B82F6] flex items-center justify-center text-white shadow-sm flex-shrink-0">
                    <Download className="w-4.5 h-4.5" />
                  </div>
                  <span className="font-semibold text-slate-800 text-[15px]">
                    Export Seamlessly
                  </span>
                </div>

                {/* Feature 4 */}
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#6366F1] flex items-center justify-center text-white shadow-sm flex-shrink-0">
                    <Users className="w-4.5 h-4.5" />
                  </div>
                  <span className="font-semibold text-slate-800 text-[15px]">
                    Team Collaboration
                  </span>
                </div>
              </div>
            </div>
          </div>
        </ScrollAnimation>
      </div>
    </section>
  );
};

export default Hero;
